import React from "react";

/* ══════════════════════════════════════════════════════════════
   PANORIX LOGO — AI-Powered Infrastructure Control Tower
   Spec: user-provided drsti-logo-{light,dark}.svg
   Concept: Hex Network — 7 inner ring nodes connected to a central
   command node with orange "D", outer ring of satellites, pulse rings.
   Theme-aware: wordmark / Devanagari / sub-tagline swap per theme.
   ══════════════════════════════════════════════════════════════ */

/* Just the hex network mark (no wordmark). Scales cleanly 20–400px. */
export function DrishtiIcon({ size = 72, className = "" }) {
  /* Source coordinates are centered around (300, 120) with satellite radius out to ~245.
     Crop box: x=175..425, y=0..250  →  viewBox "175 0 250 250" (square) */
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="175 0 250 250"
      width={size}
      height={size}
      aria-hidden="true"
      className={`dr-mark ${className}`}
    >
      {/* Outer ring connections */}
      <line x1="300" y1="28" x2="370" y2="68" stroke="#1E4E8C" strokeWidth="1" opacity="0.3" />
      <line x1="370" y1="68" x2="380" y2="148" stroke="#1E4E8C" strokeWidth="1" opacity="0.3" />
      <line x1="380" y1="148" x2="330" y2="208" stroke="#1E4E8C" strokeWidth="1" opacity="0.3" />
      <line x1="330" y1="208" x2="270" y2="208" stroke="#1E4E8C" strokeWidth="1" opacity="0.3" />
      <line x1="270" y1="208" x2="220" y2="148" stroke="#1E4E8C" strokeWidth="1" opacity="0.3" />
      <line x1="220" y1="148" x2="230" y2="68" stroke="#1E4E8C" strokeWidth="1" opacity="0.3" />
      <line x1="230" y1="68" x2="300" y2="28" stroke="#1E4E8C" strokeWidth="1" opacity="0.3" />

      {/* Connection lines to center */}
      <line x1="300" y1="120" x2="300" y2="28" stroke="#2E70AD" strokeWidth="1.2" opacity="0.5" />
      <line x1="300" y1="120" x2="370" y2="68" stroke="#2E70AD" strokeWidth="1.2" opacity="0.5" />
      <line x1="300" y1="120" x2="380" y2="148" stroke="#2E70AD" strokeWidth="1.2" opacity="0.5" />
      <line x1="300" y1="120" x2="330" y2="208" stroke="#2E70AD" strokeWidth="1.2" opacity="0.5" />
      <line x1="300" y1="120" x2="270" y2="208" stroke="#2E70AD" strokeWidth="1.2" opacity="0.5" />
      <line x1="300" y1="120" x2="220" y2="148" stroke="#2E70AD" strokeWidth="1.2" opacity="0.5" />
      <line x1="300" y1="120" x2="230" y2="68" stroke="#2E70AD" strokeWidth="1.2" opacity="0.5" />

      {/* Extended outer connections */}
      <line x1="300" y1="28" x2="272" y2="4" stroke="#5790C9" strokeWidth="0.6" opacity="0.25" />
      <line x1="300" y1="28" x2="328" y2="4" stroke="#5790C9" strokeWidth="0.6" opacity="0.25" />
      <line x1="370" y1="68" x2="410" y2="40" stroke="#5790C9" strokeWidth="0.6" opacity="0.25" />
      <line x1="380" y1="148" x2="420" y2="155" stroke="#5790C9" strokeWidth="0.6" opacity="0.25" />
      <line x1="330" y1="208" x2="350" y2="245" stroke="#5790C9" strokeWidth="0.6" opacity="0.25" />
      <line x1="270" y1="208" x2="250" y2="245" stroke="#5790C9" strokeWidth="0.6" opacity="0.25" />
      <line x1="220" y1="148" x2="180" y2="155" stroke="#5790C9" strokeWidth="0.6" opacity="0.25" />
      <line x1="230" y1="68" x2="190" y2="40" stroke="#5790C9" strokeWidth="0.6" opacity="0.25" />

      {/* Outer satellite nodes */}
      <circle cx="272" cy="4" r="4.5" fill="#143A6B" stroke="#2E70AD" strokeWidth="0.5" />
      <circle cx="328" cy="4" r="4.5" fill="#143A6B" stroke="#2E70AD" strokeWidth="0.5" />
      <circle cx="410" cy="40" r="4" fill="#143A6B" stroke="#2E70AD" strokeWidth="0.5" />
      <circle cx="420" cy="155" r="3.5" fill="#143A6B" stroke="#2E70AD" strokeWidth="0.5" />
      <circle cx="350" cy="245" r="3.5" fill="#143A6B" stroke="#2E70AD" strokeWidth="0.5" />
      <circle cx="250" cy="245" r="3.5" fill="#143A6B" stroke="#2E70AD" strokeWidth="0.5" />
      <circle cx="180" cy="155" r="3.5" fill="#143A6B" stroke="#2E70AD" strokeWidth="0.5" />
      <circle cx="190" cy="40" r="4" fill="#143A6B" stroke="#2E70AD" strokeWidth="0.5" />

      {/* Inner ring nodes */}
      <circle cx="300" cy="28" r="10" fill="#1E4E8C" stroke="#5790C9" strokeWidth="1" />
      <circle cx="370" cy="68" r="10" fill="#1E4E8C" stroke="#5790C9" strokeWidth="1" />
      <circle cx="380" cy="148" r="10" fill="#1E4E8C" stroke="#5790C9" strokeWidth="1" />
      <circle cx="330" cy="208" r="10" fill="#1E4E8C" stroke="#5790C9" strokeWidth="1" />
      <circle cx="270" cy="208" r="10" fill="#1E4E8C" stroke="#5790C9" strokeWidth="1" />
      <circle cx="220" cy="148" r="10" fill="#1E4E8C" stroke="#5790C9" strokeWidth="1" />
      <circle cx="230" cy="68" r="10" fill="#1E4E8C" stroke="#5790C9" strokeWidth="1" />

      {/* Central node */}
      <circle cx="300" cy="120" r="38" fill="#143A6B" stroke="#1E4E8C" strokeWidth="2.5" />
      <circle cx="300" cy="120" r="26" fill="#1E4E8C" />

      {/* Pulse rings */}
      <circle cx="300" cy="120" r="50" fill="none" stroke="#F26B1F" strokeWidth="0.6" opacity="0.2" />
      <circle cx="300" cy="120" r="64" fill="none" stroke="#F26B1F" strokeWidth="0.4" opacity="0.1" />

      {/* Central D */}
      <text x="300" y="122" textAnchor="middle" dominantBaseline="central" fontFamily="Inter, 'Helvetica Neue', Helvetica, Arial, sans-serif" fontSize="32" fontWeight="600" fill="#F26B1F">P</text>
    </svg>
  );
}

/* Miniature version for very small UIs (22px) — simplified to 6-node hex + orange center */
export function DrishtiIconMini({ size = 22, stroke = "currentColor", color = "#F26B1F" }) {
  const r = 9;
  const cx = size / 2;
  const cy = size / 2;
  const pts = [0, 60, 120, 180, 240, 300].map(deg => {
    const rad = (deg - 90) * Math.PI / 180;
    return [cx + Math.cos(rad) * r, cy + Math.sin(rad) * r];
  });
  return (
    <g>
      {pts.map((p, i) => {
        const next = pts[(i + 1) % pts.length];
        return <line key={`e${i}`} x1={p[0]} y1={p[1]} x2={next[0]} y2={next[1]} strokeWidth="0.8" stroke={stroke} opacity="0.45" />;
      })}
      {pts.map((p, i) => (
        <React.Fragment key={`l${i}`}>
          <line x1={cx} y1={cy} x2={p[0]} y2={p[1]} strokeWidth="0.6" stroke={stroke} opacity="0.35" />
          <circle cx={p[0]} cy={p[1]} r="1.6" fill={stroke} />
        </React.Fragment>
      ))}
      <circle cx={cx} cy={cy} r="3.2" fill={color} />
    </g>
  );
}

/* Full logo — HORIZONTAL lockup: hex network (left) + wordmark + Devanagari + tagline + sub-tagline (right).
   Matches the family pattern (Docuweave etc.). Theme-aware via CSS classes.
*/
export default function DrishtiLogo({ height = 140, className = "" }) {
  const viewW = 760;
  const viewH = 240;
  const w = Math.round(viewW * height / viewH);
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${viewW} ${viewH}`}
      width={w}
      height={height}
      role="img"
      aria-label="Panorix — AI-Powered Infrastructure Control Tower"
      className={`dr-logo-full ${className}`}
    >
      {/* ── LEFT: Hex Network icon (nested SVG keeps original coords intact) ── */}
      <svg x="0" y="0" width="240" height="240" viewBox="170 0 250 250" preserveAspectRatio="xMidYMid meet" overflow="visible">
        {/* Outer ring connections */}
        <line x1="300" y1="28" x2="370" y2="68" stroke="#1E4E8C" strokeWidth="1" opacity="0.3" />
        <line x1="370" y1="68" x2="380" y2="148" stroke="#1E4E8C" strokeWidth="1" opacity="0.3" />
        <line x1="380" y1="148" x2="330" y2="208" stroke="#1E4E8C" strokeWidth="1" opacity="0.3" />
        <line x1="330" y1="208" x2="270" y2="208" stroke="#1E4E8C" strokeWidth="1" opacity="0.3" />
        <line x1="270" y1="208" x2="220" y2="148" stroke="#1E4E8C" strokeWidth="1" opacity="0.3" />
        <line x1="220" y1="148" x2="230" y2="68" stroke="#1E4E8C" strokeWidth="1" opacity="0.3" />
        <line x1="230" y1="68" x2="300" y2="28" stroke="#1E4E8C" strokeWidth="1" opacity="0.3" />
        {/* to center */}
        <line x1="300" y1="120" x2="300" y2="28" stroke="#2E70AD" strokeWidth="1.2" opacity="0.5" />
        <line x1="300" y1="120" x2="370" y2="68" stroke="#2E70AD" strokeWidth="1.2" opacity="0.5" />
        <line x1="300" y1="120" x2="380" y2="148" stroke="#2E70AD" strokeWidth="1.2" opacity="0.5" />
        <line x1="300" y1="120" x2="330" y2="208" stroke="#2E70AD" strokeWidth="1.2" opacity="0.5" />
        <line x1="300" y1="120" x2="270" y2="208" stroke="#2E70AD" strokeWidth="1.2" opacity="0.5" />
        <line x1="300" y1="120" x2="220" y2="148" stroke="#2E70AD" strokeWidth="1.2" opacity="0.5" />
        <line x1="300" y1="120" x2="230" y2="68" stroke="#2E70AD" strokeWidth="1.2" opacity="0.5" />
        {/* extended */}
        <line x1="300" y1="28" x2="272" y2="4" stroke="#5790C9" strokeWidth="0.6" opacity="0.25" />
        <line x1="300" y1="28" x2="328" y2="4" stroke="#5790C9" strokeWidth="0.6" opacity="0.25" />
        <line x1="370" y1="68" x2="410" y2="40" stroke="#5790C9" strokeWidth="0.6" opacity="0.25" />
        <line x1="380" y1="148" x2="420" y2="155" stroke="#5790C9" strokeWidth="0.6" opacity="0.25" />
        <line x1="330" y1="208" x2="350" y2="245" stroke="#5790C9" strokeWidth="0.6" opacity="0.25" />
        <line x1="270" y1="208" x2="250" y2="245" stroke="#5790C9" strokeWidth="0.6" opacity="0.25" />
        <line x1="220" y1="148" x2="180" y2="155" stroke="#5790C9" strokeWidth="0.6" opacity="0.25" />
        <line x1="230" y1="68" x2="190" y2="40" stroke="#5790C9" strokeWidth="0.6" opacity="0.25" />
        {/* satellites */}
        <circle cx="272" cy="4" r="4.5" fill="#143A6B" stroke="#2E70AD" strokeWidth="0.5" />
        <circle cx="328" cy="4" r="4.5" fill="#143A6B" stroke="#2E70AD" strokeWidth="0.5" />
        <circle cx="410" cy="40" r="4" fill="#143A6B" stroke="#2E70AD" strokeWidth="0.5" />
        <circle cx="420" cy="155" r="3.5" fill="#143A6B" stroke="#2E70AD" strokeWidth="0.5" />
        <circle cx="350" cy="245" r="3.5" fill="#143A6B" stroke="#2E70AD" strokeWidth="0.5" />
        <circle cx="250" cy="245" r="3.5" fill="#143A6B" stroke="#2E70AD" strokeWidth="0.5" />
        <circle cx="180" cy="155" r="3.5" fill="#143A6B" stroke="#2E70AD" strokeWidth="0.5" />
        <circle cx="190" cy="40" r="4" fill="#143A6B" stroke="#2E70AD" strokeWidth="0.5" />
        {/* inner ring */}
        <circle cx="300" cy="28" r="10" fill="#1E4E8C" stroke="#5790C9" strokeWidth="1" />
        <circle cx="370" cy="68" r="10" fill="#1E4E8C" stroke="#5790C9" strokeWidth="1" />
        <circle cx="380" cy="148" r="10" fill="#1E4E8C" stroke="#5790C9" strokeWidth="1" />
        <circle cx="330" cy="208" r="10" fill="#1E4E8C" stroke="#5790C9" strokeWidth="1" />
        <circle cx="270" cy="208" r="10" fill="#1E4E8C" stroke="#5790C9" strokeWidth="1" />
        <circle cx="220" cy="148" r="10" fill="#1E4E8C" stroke="#5790C9" strokeWidth="1" />
        <circle cx="230" cy="68" r="10" fill="#1E4E8C" stroke="#5790C9" strokeWidth="1" />
        {/* central */}
        <circle cx="300" cy="120" r="38" fill="#143A6B" stroke="#1E4E8C" strokeWidth="2.5" />
        <circle cx="300" cy="120" r="26" fill="#1E4E8C" />
        <circle cx="300" cy="120" r="50" fill="none" stroke="#F26B1F" strokeWidth="0.6" opacity="0.2" />
        <circle cx="300" cy="120" r="64" fill="none" stroke="#F26B1F" strokeWidth="0.4" opacity="0.1" />
        <text x="300" y="122" textAnchor="middle" dominantBaseline="central" fontFamily="Inter, 'Helvetica Neue', Helvetica, Arial, sans-serif" fontSize="32" fontWeight="600" fill="#F26B1F">P</text>
      </svg>

      {/* ── RIGHT: text column starts at x=260 ── */}
      {/* WORDMARK — family style (italic Playfair Display) */}
      <text x="260" y="126" fontFamily="'Playfair Display', Georgia, serif" fontSize="82" fontWeight="400" fontStyle="italic" letterSpacing="-0.02em">
        <tspan className="dr-wordmark-text">Pano</tspan>
        <tspan fill="#F26B1F" fontStyle="italic" fontWeight="400">r</tspan>
        <tspan className="dr-wordmark-text">ix</tspan>
      </text>
    </svg>
  );
}
